import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormHelper } from '@lingoda/forms';
import { CouponField, Form, Formik, handleFormSubmit, useFormContext } from '@lingoda/forms';
import {
    getApolloErrorUiMessage,
    getValidationErrorMessages,
    isApolloError,
} from '@lingoda/monolith-graphql';
import { getCookieValue, removeCookieValue } from '@lingoda/cookie';
import { useHistory, useLocation } from '@lingoda/router';
import { getQueryParams } from '@lingoda/urls';
import { useGetLifetimeDiscountCoupon } from '@lingoda/subscriptions';

export interface FormValues {
    couponCode: string;
}
interface OwnProps {
    couponCode?: string;
    couponDescription?: string;
    onChange: (couponCode: string) => Promise<unknown>;
    onAsyncValidate: (asyncValidating: boolean) => void;
    keepInputAfterApply?: boolean;
}

const COUPON_COOKIE_NAME = 'couponCode';
const COUPON_PARAM_NAME = 'couponCode';

export const CouponForm = ({
    couponCode,
    couponDescription,
    onChange,
    onAsyncValidate,
    keepInputAfterApply,
}: OwnProps) => {
    const handleOnSubmit = useMemo(
        () =>
            handleFormSubmit<unknown, FormValues>(
                async ({ couponCode: newCouponCode }: FormValues, form: FormHelper<FormValues>) => {
                    form.setSubmitting(true);
                    onAsyncValidate(true);
                    await onChange(newCouponCode);
                },
                onCouponError,
                (_: FormValues, form: FormHelper<FormValues>) => {
                    onAsyncValidate(false);
                    form.setSubmitting(false);
                },
            ),
        [onAsyncValidate, onChange],
    );

    return (
        <Formik initialValues={{ couponCode: couponCode ?? '' }} onSubmit={handleOnSubmit}>
            <Form data-cy="coupon-form">
                <CouponFormContent
                    onChange={onChange}
                    onAsyncValidate={onAsyncValidate}
                    couponCode={couponCode}
                    couponDescription={couponDescription}
                    keepInputAfterApply={keepInputAfterApply}
                />
            </Form>
        </Formik>
    );
};

const CouponFormContent = ({ couponCode, couponDescription, keepInputAfterApply }: OwnProps) => {
    useApplyCoupon(couponCode);

    return (
        <CouponField
            name="couponCode"
            coupon={couponCode}
            couponDescription={couponDescription}
            reveal
            keepInputAfterApply={keepInputAfterApply}
        />
    );
};

const useApplyCoupon = (currentCoupon?: string) => {
    const history = useHistory();
    const { search } = useLocation();
    const { submitForm, setFieldValue } = useFormContext<FormValues>();
    const { couponCode: coupon } = getQueryParams<{
        couponCode: string;
    }>(search);

    const [shouldSubmit, setShouldSubmit] = useState(false);

    const removeCouponFromUrl = useCallback(() => {
        const urlSearchParams = new URLSearchParams(search);
        if (!urlSearchParams.has(COUPON_PARAM_NAME)) {
            return;
        }
        urlSearchParams.delete(COUPON_PARAM_NAME);
        history.replace({ search: urlSearchParams.toString() });
    }, [history, search]);

    const couponString = coupon?.toString();

    const lifetimeCouponData = useGetLifetimeDiscountCoupon();
    const lifetimeDiscountCoupon = !lifetimeCouponData.loading
        ? lifetimeCouponData.couponCode
        : undefined;

    useEffect(() => {
        const applyCouponCookie = async () => {
            const value =
                lifetimeDiscountCoupon ?? couponString ?? getCookieValue(COUPON_COOKIE_NAME);
            if (!value || value === currentCoupon) {
                return;
            }

            if (!lifetimeDiscountCoupon) {
                removeCookieValue(COUPON_COOKIE_NAME);
            }

            await setFieldValue('couponCode', value);
            setShouldSubmit(true);
            removeCouponFromUrl();
        };

        void applyCouponCookie();
    }, [
        couponString,
        currentCoupon,
        lifetimeDiscountCoupon,
        removeCouponFromUrl,
        setFieldValue,
        submitForm,
    ]);

    useEffect(() => {
        if (shouldSubmit) {
            void submitForm();
            setShouldSubmit(false);
        }
    }, [shouldSubmit, submitForm]);
};

const onCouponError = (errors: unknown, _: FormValues, form: FormHelper<FormValues>) => {
    if (isApolloError(errors)) {
        const couponErrors = getValidationErrorMessages(errors);
        const validationError = couponErrors['couponCode'];
        const otherErrorMessage = getApolloErrorUiMessage(errors);

        const errorText: string | undefined = validationError ?? otherErrorMessage;

        if (errorText) {
            form.setErrors({ couponCode: errorText });
        }
    }
};
